/**
 * La gravure du niveau, calculée une fois, en pixels.
 *
 * Pur : ni React, ni DOM, ni transport. Ce module prend une frise et un zoom,
 * et rend **tout** ce que la partition dessine, chaque objet à son abscisse et
 * à son ordonnée définitives. Le moteur de `score-engine.ts` ne fait ensuite
 * que poser des nœuds là où on le lui dit ; il ne calcule plus rien.
 *
 * ## Pourquoi la gravure a quitté le composant
 *
 * Elle y était refaite **à chaque rendu**, et un rendu tombait à chaque temps :
 * la tête de lecture se promeut en état React quand elle franchit une borne, et
 * ce rendu-là reparcourait toutes les frappes du niveau — `friezeSteps` sur les
 * deux mains, puis `strikeLead` sur chaque segment — avant de jeter les
 * quatre-vingt-dix pour cent qui ne sont pas à l'écran. Le coût d'une image
 * dépendait donc de la **longueur du morceau**, et il retombait exactement sur
 * le temps, c'est-à-dire à l'endroit où un à-coup s'entend.
 *
 * Ici, ce parcours a lieu une fois par frise. Ce qui reste à faire pendant que
 * la musique joue est borné par ce qui est **visible**, et ne dépend plus de
 * rien d'autre.
 *
 * ## Le grain, et pourquoi il y en a un
 *
 * Les objets sont rangés dans des colonnes de largeur fixe (`BUCKET_PX`) :
 * chacune liste ce qui la traverse. Trouver ce qui est à l'écran est alors une
 * lecture de quelques colonnes, et non un filtrage de la frise entière. C'est
 * la seule structure du fichier qui existe pour la vitesse, et elle est ici
 * plutôt que dans le moteur parce qu'elle se teste sans navigateur.
 *
 * ## Les couleurs sont dedans, et c'est voulu
 *
 * Un objet gravé porte sa teinte comme il porte son abscisse. Le moteur n'a
 * donc aucune palette à connaître, et les deux seuls endroits qui décident de
 * quoi que ce soit — ce module et `staff.ts` — sont tous les deux vérifiables
 * sans écran.
 */

import {
  JOURNEY_HINT_COLOR_LEFT_DISCOVER,
  JOURNEY_HINT_COLOR_LEFT_PRACTICE,
  JOURNEY_HINT_COLOR_RIGHT_DISCOVER,
  JOURNEY_HINT_COLOR_RIGHT_PRACTICE,
} from "@/hooks/journey/journey-hint-mapping";
import { GOLD, GREEN, RED, TEXT_MUTE, VIOLET } from "@/components/ui/organisms/chord-practice/palette";
import { GLYPHS, UNITS_PER_SPACE, type Accidental } from "@/lib/harmony/glyphs";
import { signatureAdvanceSteps, signatureSteps, type Clef, type KeySignature } from "@/lib/harmony/keysignature";
import { positionShort } from "@/lib/harmony/position";
import type { Frieze, FriezeKeyMark, FriezeLabel, FriezeSegment } from "@/lib/harmony/frieze";
import { friezeSteps } from "@/lib/harmony/frieze";
import type { KeyRelation } from "@/lib/harmony/key-spans";
import type { RhythmMark } from "@/lib/harmony/rhythm";
import { BASS, TREBLE, fitStep, ledgerSteps, lineSteps, staffSpan, topStep, type Staff } from "@/lib/harmony/staff";
import type { Ottava } from "@/lib/harmony/ottava";

// ── Les couleurs : le meuble, jamais la musique ──────────────────

/**
 * Le turquoise de la main gauche, celui du clavier du bas et de la portée de fa.
 *
 * Exporté parce que d'autres objets d'une seule main le reprennent : une seule
 * teinte pour la gauche partout, et rien à expliquer.
 */
export const LEFT_HAND_COLOR = hex(JOURNEY_HINT_COLOR_LEFT_PRACTICE);

/** Le violet de la main droite, celui des notes de l'accord et de la portée de sol. */
export const RIGHT_HAND_COLOR = hex(JOURNEY_HINT_COLOR_RIGHT_PRACTICE);

/**
 * La teinte claire de chaque main, pour la note déjà jouée.
 *
 * Les deux couples du produit, sans en inventer un : la teinte de travail pour
 * ce qui reste à faire, la teinte de découverte pour ce qui est fait. Même
 * hue, un cran plus clair, ce qui se voit du coin de l'oeil sans demander une
 * seconde couleur à apprendre.
 */
const LEFT_LIT = hex(JOURNEY_HINT_COLOR_LEFT_DISCOVER);
const RIGHT_LIT = hex(JOURNEY_HINT_COLOR_RIGHT_DISCOVER);

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/**
 * Les gris de la partition : le meuble, jamais la musique.
 *
 * **Opaques, et c'est la règle du fichier.** Une partition est faite d'objets
 * qui se croisent : la barre de mesure traverse les cinq lignes, la tête de
 * lecture traverse tout, sa pointe recouvre son propre trait. Deux blancs
 * translucides qui se superposent s'additionnent, et chaque croisement devient
 * un point plus clair que ses deux traits. On lit alors une grille de nœuds là
 * où il n'y a que des lignes.
 *
 * Chaque gris est donc **mélangé au fond** plutôt que posé dessus : même
 * teinte à l'œil, aucune addition au croisement. `color-mix` le fait sur le
 * jeton du produit, ce qui garde le lien avec le fond si celui-ci change.
 */
export const grey = (percent: number) => `color-mix(in srgb, #fff ${percent}%, var(--color-bg-deep))`;

export const STAFF_LINE = grey(16);
export const LEDGER_LINE = grey(30);
const BAR_LINE = grey(22);
const BEAT_DOT = grey(16);
const BAR_DOT = grey(34);
export const HEAD_LINE = grey(52);
export const HEAD_TIP = grey(62);
export const SEPARATOR = grey(24);

/**
 * Ce qu'une note garde de sa couleur tant qu'elle n'est pas jouée.
 *
 * Une teinte, jamais une transparence. Une note à moitié transparente laisse
 * passer les lignes de la portée **au travers d'elle** : elle cesse d'être un
 * objet posé sur la portée pour devenir une tache, et deux notes qui se
 * croisent verticalement se mélangent. Un mélange avec le fond donne la même
 * hiérarchie, en gardant la note opaque et sa forme nette.
 *
 * Sur une portée le corps **est** la note : sa longueur est la durée, sa
 * hauteur est la note, et il n'y a rien derrière lui pour la porter. Il ne peut
 * donc pas s'effacer autant que le corps des voies d'avant, où l'information
 * était l'attaque.
 */
const NOTE_AHEAD_MIX = 76;

const ahead = (color: string) => `color-mix(in srgb, ${color} ${NOTE_AHEAD_MIX}%, var(--color-bg-deep))`;

const KEY_MARK_LINE = `color-mix(in srgb, ${VIOLET} 60%, var(--color-bg-deep))`;
const KEY_MARK_LINE_HOME = grey(34);

const KEY_RELATION_WORD: Record<KeyRelation, string> = {
  home: "retour",
  relative: "relatif",
  parallel: "homonyme",
  other: "modulation",
};

/**
 * Les trois crans de l'échelle des positions, par rang de la fondamentale.
 *
 * Les mêmes que la pastille au-dessus des notes, et pour la même raison : ce
 * sont déjà les trois couleurs que le produit emploie quand il ordonne quelque
 * chose. Elles ne jugent rien, elles ordonnent, et c'est ce qu'une position
 * est : la fondamentale s'éloigne de la basse d'un cran à chaque fois.
 */
export const INVERSION_COLOR: Record<number, string> = {
  0: TEXT_MUTE,
  1: GREEN,
  2: GOLD,
  3: RED,
};

// ── Les mesures du rail ──────────────────────────────────────────

/** Le corps des petites capitales du rail, et la hauteur de sa ligne d'en-tête. */
export const HEADER_HEIGHT_PX = 10;
export const RAIL_GAP_PX = 5;

/**
 * La place réservée au chiffrage d'accord, au-dessus de la portée de droite.
 *
 * Réservée et non prise sur la portée : le bloc a deux lignes, et une hauteur
 * qui dépendrait des lignes supplémentaires du moment ferait sauter tout le
 * rail d'un accord à l'autre.
 */
export const CHORD_BLOCK_PX = 50;
export const CHORD_NAME_PX = 31;
export const CHORD_NOTES_PX = 14;
export const CHORD_POSITION_PX = 11;

/** Le corps d'un nom de touche, sur sa plaque. */
export const BASS_NAME_PX = 20;
/** Le corps du compte de tours, qui annote la figure au lieu de la nommer. */
export const LOOP_SIZE_PX = 12;

/**
 * Ce qui sépare un nom de la frappe qu'il nomme.
 *
 * À droite d'elle et non centré dessus : un nom vaut **à partir de** l'instant
 * qu'il désigne, et l'écrire à droite est ce qui le dit. C'est aussi ce qui le
 * met à droite de la tête de lecture quand il lui est collé.
 */
export const NAME_OFFSET_PX = 12;

/**
 * Le blanc gardé entre un nom et la fin de son étendue.
 *
 * Un nom vaut jusqu'au suivant, donc il ne doit jamais mordre sur lui. C'est
 * cette réserve qui arrête le nom collé à la tête de lecture : il glisse avec
 * elle tant qu'il tient dans son étendue, puis il **s'arrête**, passe à gauche
 * de la tête, et s'en va avec le ruban depuis la fin de sa propre note.
 */
export const NAME_TAIL_PX = 8;

/** La règle du bas : les temps, les mesures, et leurs numéros. */
const BEATS_HEIGHT_PX = 15;

/**
 * L'écart entre les deux portées.
 *
 * Assez pour qu'on ne les confonde pas en un seul système de dix lignes, pas
 * plus : ce sont les deux mains d'un même instant, et l'œil doit pouvoir les
 * lire d'un seul balayage vertical.
 */
const STAVES_GAP_PX = 16;

/**
 * La place qu'on accorde à l'empilement des degrés, les deux portées réunies.
 *
 * Un budget et non une hauteur : l'étendue à graver vient du morceau, pas d'un
 * réglage, et deux niveaux du même morceau n'ont pas le même ambitus. C'est la
 * **taille du degré** qui s'ajuste, entre les bornes ci-dessous, et la hauteur
 * du rail suit. Elle est donc constante pendant tout un niveau, ce qui est la
 * seule chose qui compte : elle ne bouge jamais pendant qu'on joue.
 */
const STEPS_BUDGET_PX = 210;

/**
 * Les tailles de degré admises, de la plus grande à la plus petite.
 *
 * Sept est la valeur de référence : c'est un interligne de quatorze pixels,
 * donc une portée aux proportions d'une partition gravée, et c'est ce qui rend
 * la note aussi épaisse qu'elle doit l'être. Les deux tailles au-dessous ne
 * servent qu'aux morceaux dont l'ambitus dépasse le budget, et elles rétrécissent
 * tout ensemble plutôt que de couper une note.
 *
 * En pixels entiers, pour que les cinq lignes tombent au pixel : une portée
 * dont les lignes sont à des demi-pixels est floue, et le flou d'une grille
 * régulière se voit beaucoup plus que celui d'un trait isolé.
 */
const STEP_SIZES = [7, 6, 5] as const;

/** L'épaisseur d'une note, plafonnée à un degré pour ne pas mordre sur sa voisine. */
const NOTE_HEIGHT_PX = 7;

/**
 * Le blanc autour d'une note : avant l'attaque, et après la tenue.
 *
 * Celui d'après ne dit pas un silence, il dit que ce sont **deux** frappes :
 * deux corps qui se touchent se lisent comme une seule tenue, ce qui est
 * exactement l'inverse de ce que la partition montre.
 *
 * Celui d'avant existe pour la **barre de mesure**. Une note posée au tick
 * exact du premier temps vient se coller à la barre, et les deux se lisent
 * alors comme un seul objet en L. Une partition gravée laisse toujours cet air
 * entre la barre et la première note, et pour la même raison. Il déplace la
 * note de cinq pixels par rapport à son instant, ce qui est le prix, et il est
 * payé du bon côté : la tête de lecture atteint la barre juste avant la note,
 * comme l'œil le fait en lisant.
 */
const NOTE_LEAD_IN_PX = 12;
const NOTE_TAIL_GAP_PX = 16;

/** La longueur d'une ligne supplémentaire, de part et d'autre de sa note. */
const LEDGER_WIDTH_PX = 24;
const LEDGER_OVERHANG_PX = 4;

/**
 * La taille du signe d'octave, en part de sa taille gravée.
 *
 * Bravura le dessine haut de près de deux interlignes, la taille d'une page
 * imprimée. Le rail est plus serré qu'une page : à cette taille, le signe et
 * son blanc reprendraient une bonne part de la place qu'il fait gagner à la
 * portée. Aux deux tiers, il se lit encore d'un coup d'œil, à la hauteur des
 * noms du rail.
 */
const OTTAVA_SCALE = 0.65;

/** Le blanc entre les notes d'un passage à l'octave et son signe : un interligne. */
const OTTAVA_CLEARANCE_STEPS = 2;

/** Les tirets de la ligne d'octave, en pixels : le trait, puis le blanc. */
export const OTTAVA_DASH_PX = 6;
export const OTTAVA_GAP_PX = 4;

/**
 * Ce que le signe laisse de ligne avant le crochet, quand il s'arrête.
 *
 * Le signe glisse avec la tête jusqu'à la fin du passage, puis il reste là. Il
 * s'arrête donc avant le crochet et non dessus : sa plaque cacherait le seul
 * trait qui dit où l'octave finit.
 */
const OTTAVA_HOOK_CLEAR_PX = 10;

/**
 * Le blanc autour de la clé, en pixels.
 *
 * Fixe et non proportionnel au degré : c'est une marge de mise en page, pas
 * une distance de solfège, et elle doit rester lisible quand la portée se
 * resserre.
 */
const LEGEND_PAD_PX = 6;

/** Ce qui sépare une altération de la note qu'elle altère, en interlignes. */
const SIGN_TO_NOTE_SPACES = 0.25;

/** Le blanc laissé après une barre de mesure avant d'y poser quoi que ce soit. */
const BAR_CLEARANCE_PX = 10;

/**
 * La part d'un emplacement que les blancs n'ont pas le droit de prendre.
 *
 * L'entrée et la queue sont des constantes en pixels, parce qu'un blanc se juge
 * à l'œil et non en fractions de temps. Mais une constante généreuse mangerait
 * une double-croche entière : à cent quatre pixels le temps, son emplacement
 * fait vingt-six pixels, et vingt-huit de blanc n'y tiennent pas.
 *
 * Les deux blancs se resserrent donc ensemble, du même facteur, dès qu'ils
 * passeraient cette part-là. Les notes longues gardent l'air qu'on leur a
 * donné, les rapides gardent leur corps, et le rapport de l'entrée à la queue
 * ne change jamais.
 */
const INK_SHARE = 0.5;

/**
 * Le zoom de la frise : ce que vaut un temps, en pixels.
 *
 * Par **temps** et non par tick ni par mesure : la résolution du morceau
 * (`ppq`) et son chiffrage ne doivent pas changer la densité de ce qu'on lit.
 * Un huit-croches est aussi serré dans un 3/4 que dans un 4/4, ce qui est la
 * seule chose que l'œil mesure.
 */
export const PX_PER_BEAT = 104;

/** Ce que le trait d'une tonalité dépasse au-dessus de la portée du haut. */
const KEY_MARK_REACH_PX = 8;
/**
 * D'où le nom commence, à droite du trait : après le numéro de mesure, qui
 * tient trois chiffres au plus dans sa police.
 */
export const KEY_MARK_NAME_LEFT_PX = 30;
/** La place qu'il faut au nom pour être écrit en entier. */
const KEY_MARK_MIN_ROOM_PX = 170;

/**
 * Où la tête de lecture se cloue, en part de la largeur.
 *
 * À gauche et non au milieu. Ce qui est **derrière** ne se lit pas : il est
 * joué, et il n'est là que pour dire d'où l'on vient. Ce qui est devant est
 * tout ce qu'on lit, et sur une partition on en lit loin : à vingt-huit pour
 * cent, il en reste presque trois fois plus à droite qu'au milieu, pour la
 * même largeur d'écran.
 */
export const HEAD_AT = 0.28;

/**
 * La largeur d'une colonne de l'index, en pixels.
 *
 * Assez fine pour que la fenêtre n'en prenne qu'une quinzaine, assez large pour
 * qu'une note tenue n'en traverse pas cinquante. Deux temps et demi : c'est
 * l'ordre de grandeur d'une mesure, donc de ce qui entre et sort ensemble.
 */
export const BUCKET_PX = 256;

// ── Ce que la gravure rend ───────────────────────────────────────

/** Une portée montée : sa place dans le rail, sa main, et ses couleurs. */
export type StavePlace = {
  readonly hand: "left" | "right";
  /**
   * La clé de la portée, qui a remplacé la lettre qu'on écrivait dans la marge.
   *
   * `MD` et `MG` étaient là faute de clé : ils disaient de quelle main était la
   * portée parce que rien d'autre ne le disait. Une clé le dit mieux, et à
   * quelqu'un qui lit elle le dit d'un coup d'œil.
   */
  readonly clef: Clef;
  readonly staff: Staff;
  readonly segments: readonly FriezeSegment[];
  /** Les passages de cette portée écrits à l'octave. Voir `ottava.ts`. */
  readonly ottavas: readonly Ottava[];
  /** Le degré le plus aigu à graver, qui donne l'origine verticale. */
  readonly highStep: number;
  readonly top: number;
  readonly height: number;
  readonly color: string;
  readonly lit: string;
};

/** Ce que le rail occupe, et où chacune de ses lignes se pose. */
export type ScoreMetrics = {
  readonly stepPx: number;
  readonly noteHeight: number;
  readonly staves: readonly StavePlace[];
  /** Où le bloc d'accord se pose, au-dessus de la portée de droite. */
  readonly chordTop: number;
  readonly beatsTop: number;
  readonly viewHeight: number;
  readonly height: number;
  /** L'orthographe de la tonique, reprise de la frise. */
  readonly keyTonic: string | null;
  /** L'armure des deux portées, reprise de la frise. */
  readonly signature: KeySignature | null;
  /** La durée d'une mesure, reprise de la frise. */
  readonly barTicks: number;
  /**
   * La largeur de la tête de système, clé et armure comprises.
   *
   * Calculée et non fixée : elle dépend de la taille du degré, que l'ambitus
   * du niveau fait varier, et du nombre d'altérations à l'armure, qui va de
   * zéro à sept. Une constante aurait été trop large pour do majeur et trop
   * étroite pour do dièse.
   */
  readonly legendWidth: number;
};

/**
 * L'ordre dans lequel les objets se peignent, et il compte.
 *
 * Les barres sont le quadrillage ; les lignes supplémentaires appartiennent à la
 * note qu'elles portent, et une ligne tracée après sa note la couperait en deux ;
 * la note passe par-dessus les deux ; les noms passent par-dessus tout, parce
 * qu'une plaque de touche se pose **sur** la portée et sur ce qui s'y joue.
 *
 * ## Pourquoi un nombre, et non l'ordre du document
 *
 * Le moteur ne monte pas les objets dans l'ordre de cette liste : il monte ce
 * qui entre dans la fenêtre, colonne par colonne, et un nom dont l'étendue
 * commence loin à gauche entre donc avant les notes de la colonne suivante. À
 * s'en remettre à l'ordre du document, la plaque « Si » finissait sous les notes
 * qu'elle doit couvrir.
 *
 * Le rang dit aussi **dans quelle couche** l'objet va : au-delà de `INK_MAX`,
 * il est au-dessus du ruban allumé, sans quoi la copie allumée d'une note
 * passerait devant les noms. Voir l'en-tête de `score-engine.ts`.
 */
export const RANK = {
  bar: 1,
  ledger: 2,
  sign: 3,
  note: 4,
  ruler: 5,
  label: 6,
  key: 7,
} as const;

/** Le dernier rang qui appartient à l'encre, c'est-à-dire au ruban du dessous. */
export const INK_MAX = RANK.note;

/** Ce que tout objet gravé porte : son étendue, et son rang de peinture. */
type Extent = {
  /** Le bord gauche, en pixels de frise. */
  readonly left: number;
  /** Le bord droit, au plus. Sert au découpage, jamais au dessin. */
  readonly right: number;
  /** Où il se peint dans la pile. Voir `RANK`. */
  readonly rank: number;
};

/** Un trait vertical : barre de mesure, ou trait d'une tonalité. */
export type RuleItem = Extent & {
  readonly kind: "rule";
  readonly top: number;
  readonly height: number;
  readonly color: string;
};

/** Une ligne supplémentaire, sous ou sur la portée. */
export type LedgerItem = Extent & {
  readonly kind: "ledger";
  readonly top: number;
  readonly width: number;
  readonly color: string;
};

/**
 * La ligne d'un passage écrit à l'octave : des tirets, et le crochet qui la
 * ferme.
 *
 * Le signe lui-même n'en fait pas partie : il se colle à la tête de lecture
 * comme un nom, et c'est un `LabelItem`. Voir `ottavaLabels`.
 */
export type OctaveItem = Extent & {
  readonly kind: "octave";
  readonly top: number;
  readonly width: number;
  /** La longueur du crochet, donc la hauteur de la boîte. */
  readonly height: number;
  /** Le crochet monte depuis la ligne (8vb), au lieu de descendre (8va). */
  readonly up: boolean;
  readonly color: string;
};

/** Un signe gravé : une altération, posée par l'origine de son contour. */
export type SignItem = Extent & {
  readonly kind: "sign";
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly viewBox: string;
  readonly path: string;
  readonly color: string;
};

/**
 * Un corps de note, dans ses deux teintes.
 *
 * Les deux, et non celle du moment : le moteur grave la note **deux fois**, une
 * fois dans chaque ruban, et c'est le découpage à la tête de lecture qui décide
 * laquelle se voit. Rien n'a donc à changer de couleur quand la tête passe, et
 * l'allumage ne coûte plus une seule repeinture. Voir l'en-tête de
 * `score-engine.ts`.
 */
export type NoteItem = Extent & {
  readonly kind: "note";
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly radius: number;
  /** Sa teinte tant qu'elle n'est pas jouée, et celle d'après. */
  readonly color: string;
  readonly litColor: string;
};

/** Un point de la règle : un temps, ou une mesure. */
export type BeatItem = Extent & {
  readonly kind: "beat";
  readonly top: number;
  readonly size: number;
  readonly color: string;
};

/** Le numéro d'une mesure, sous la règle. */
export type MeasureItem = Extent & {
  readonly kind: "measure";
  readonly top: number;
  readonly text: string;
};

/**
 * Un nom posé sur le ruban : un chiffrage, une plaque de touche, une tonalité.
 *
 * ## Un nom a trois vies, et deux ancrages
 *
 * Devant la tête, il attend **à sa frappe**, calé par son bord gauche : posé
 * là, il s'étend sur les notes qu'il couvre, ce qui est exactement ce qu'il
 * dit. Sous la tête, il lui est collé, et c'est la couche fixe qui le dessine —
 * le ruban le tait. Derrière elle, il est calé sur la **fin** de son étendue,
 * par son bord droit, exactement là où le nom collé l'a lâché : le passage de
 * l'un à l'autre ne saute donc pas.
 *
 * Par le bord droit, et c'est ce qui permet de s'en tenir là sans jamais
 * mesurer le nom : dans un ruban de largeur nulle, `right` place le bord droit
 * à une abscisse connue.
 *
 * ## Un compte de tours ne bouge jamais
 *
 * « ×4 » n'est pas un nom qui vaut jusqu'au suivant, c'est une marque posée là
 * où la figure recommence. Il reste à sa frappe, avant comme après, et il n'est
 * jamais collé à la tête : `passedRight` est alors nul.
 */
export type LabelItem = Extent & {
  readonly kind: "label";
  /**
   * La famille du nom : un seul nom par famille se colle à la tête.
   *
   * Une famille par portée pour les signes d'octave, et non une seule : les
   * deux mains peuvent être écrites à l'octave en même temps, et chacune doit
   * garder son signe sous les yeux.
   */
  readonly family: "chord" | "bass" | "key" | "ottavaRight" | "ottavaLeft";
  readonly fromTick: number;
  readonly untilTick: number;
  /** L'abscisse du bord gauche, tant que la tête n'a pas passé la fin. */
  readonly aheadLeft: number;
  /** L'abscisse du bord droit une fois la fin passée, ou `null` s'il ne bouge pas. */
  readonly passedRight: number | null;
  readonly top: number;
  /** La boîte est centrée sur sa ligne plutôt que posée dessous. */
  readonly centerY: boolean;
  /**
   * La place que la fenêtre du nom lui laisse, en pixels.
   *
   * Même en chiffrage, une fenêtre d'un temps est étroite : quatre-vingts
   * pixels pour `Ré m` et sa position. Ce qui cède se cède dans l'ordre :
   * d'abord la position, qui précise le geste ; puis le nom entier, faute de
   * mieux. Le moteur mesure et tranche ; ici on ne dit que la place.
   */
  readonly roomPx: number;
  /** Ce que le nom écrit. Le moteur en fait des nœuds, il n'en décide rien. */
  readonly text: LabelText;
};

/** Ce qu'un nom écrit, une fois pour toutes, sans rien savoir du DOM. */
export type LabelText =
  | {
      readonly of: "chord";
      readonly name: string;
      /** La basse écrite après la barre, quand ce n'est pas la fondamentale. */
      readonly slash: string | null;
      /** La position, première chose qui saute quand la fenêtre est courte. */
      readonly position: string | null;
      readonly positionColor: string;
      readonly notes: readonly { readonly name: string; readonly root: boolean }[];
    }
  | {
      readonly of: "bass";
      readonly name: string;
      /** Un compte de tours annote la figure au lieu de la nommer. */
      readonly loop: boolean;
    }
  | {
      readonly of: "key";
      readonly name: string;
      readonly away: boolean;
    }
  | {
      readonly of: "ottava";
      /** « 8va » ou « 8vb », pour qui lit le nœud et non le dessin. */
      readonly name: string;
      readonly path: string;
      readonly viewBox: string;
      readonly width: number;
      readonly height: number;
      readonly color: string;
    };

export type ScoreItem = RuleItem | LedgerItem | OctaveItem | SignItem | NoteItem | BeatItem | MeasureItem | LabelItem;

/** La gravure entière d'un niveau : ses mesures, ses objets, et leur index. */
export type ScoreLayout = {
  readonly metrics: ScoreMetrics;
  /** Ce qu'un tick vaut en pixels de frise. */
  readonly zoom: number;
  readonly fromTick: number;
  readonly toTick: number;
  /** Tous les objets du ruban, dans l'ordre où ils doivent être peints. */
  readonly items: readonly ScoreItem[];
  /** Les noms de chaque famille, dans l'ordre du temps. */
  readonly labels: Readonly<Record<LabelItem["family"], readonly LabelItem[]>>;
  /** Les colonnes de l'index : ce que chacune traverse. */
  readonly buckets: readonly (readonly ScoreItem[])[];
  /** Les cinq lignes de chaque portée, qui appartiennent au cadre. */
  readonly staffLines: readonly { readonly top: number }[];
  /** Les signes de la marge : les deux clés, et l'armure. */
  readonly legend: readonly SignItem[];
};

// ── La gravure ───────────────────────────────────────────────────

/**
 * Graver un niveau : ses mesures, puis tous ses objets.
 *
 * Appelée une fois par frise. Tout ce qui suit, pendant que la musique joue,
 * ne fait que lire ce qu'elle a produit.
 */
export function layoutScore(frieze: Frieze, beatTicks: number): ScoreLayout {
  const zoom = beatTicks > 0 ? PX_PER_BEAT / beatTicks : 0;
  const metrics = measure(frieze);
  const x = (tick: number) => (tick - frieze.fromTick) * zoom;

  const items: ScoreItem[] = [];

  // L'ordre de peinture est celui du document, et il compte : les barres sont
  // le quadrillage, les lignes supplémentaires appartiennent à la note qu'elles
  // portent, et la note passe par-dessus les deux. Une ligne supplémentaire
  // tracée après sa note la couperait en deux.
  for (const stave of metrics.staves) {
    bars(items, frieze.marks, metrics, stave, x);
  }

  for (const stave of metrics.staves) {
    voice(items, metrics, stave, x);
    octaveLines(items, metrics, stave, x);
  }

  ruler(items, frieze.marks, metrics, x);

  const labels = {
    chord: chordLabels(frieze.chords, metrics, x),
    bass: bassLabels(frieze.bass, metrics, x),
    key: keyLabels(frieze.keys, metrics, x),
    ottavaRight: ottavaLabels(metrics, metrics.staves[0]!, x),
    ottavaLeft: ottavaLabels(metrics, bassOf(metrics), x),
  };

  keyRules(items, frieze.keys, metrics, x);
  items.push(...labels.chord, ...labels.bass, ...labels.key, ...labels.ottavaRight, ...labels.ottavaLeft);

  return {
    metrics,
    zoom,
    fromTick: frieze.fromTick,
    toTick: frieze.toTick,
    items,
    labels,
    buckets: index(items),
    staffLines: metrics.staves.flatMap((stave) =>
      lineSteps(stave.staff).map((step) => ({ top: linePx(metrics, stave, step) })),
    ),
    legend: legendSigns(metrics),
  };
}

/**
 * La taille d'un degré, et la place de chaque ligne.
 *
 * L'ambitus vient du morceau : c'est lui qui décide de la taille du degré, donc
 * de la hauteur du rail. Elle est constante pendant tout un niveau, ce qui est
 * la seule chose qui compte — elle ne bouge jamais pendant qu'on joue.
 */
function measure(frieze: Frieze): ScoreMetrics {
  const spans = {
    right: withOttavas(staffSpan(friezeSteps(frieze.right), TREBLE), frieze.ottavas.right, TREBLE),
    left: withOttavas(staffSpan(friezeSteps(frieze.left), BASS), frieze.ottavas.left, BASS),
  };

  const stepPx = fitStep({ spans: [spans.right, spans.left], budget: STEPS_BUDGET_PX, sizes: STEP_SIZES });
  // Plafonnée par le degré : deux notes voisines d'une seconde doivent garder
  // un cheveu de noir entre elles au lieu de fondre en une masse.
  const noteHeight = Math.min(NOTE_HEIGHT_PX, stepPx);

  const trebleTop = CHORD_BLOCK_PX;
  const trebleHeight = (spans.right.high - spans.right.low) * stepPx + noteHeight;
  const bassTop = trebleTop + trebleHeight + STAVES_GAP_PX;
  const bassHeight = (spans.left.high - spans.left.low) * stepPx + noteHeight;
  const beatsTop = bassTop + bassHeight + RAIL_GAP_PX;
  const viewHeight = beatsTop + BEATS_HEIGHT_PX;

  return {
    stepPx,
    noteHeight,
    staves: [
      {
        hand: "right",
        clef: "treble",
        staff: TREBLE,
        segments: frieze.right,
        ottavas: frieze.ottavas.right,
        highStep: spans.right.high,
        top: trebleTop,
        height: trebleHeight,
        color: RIGHT_HAND_COLOR,
        lit: RIGHT_LIT,
      },
      {
        hand: "left",
        clef: "bass",
        staff: BASS,
        segments: frieze.left,
        ottavas: frieze.ottavas.left,
        highStep: spans.left.high,
        top: bassTop,
        height: bassHeight,
        color: LEFT_HAND_COLOR,
        lit: LEFT_LIT,
      },
    ],
    chordTop: 0,
    beatsTop,
    viewHeight,
    height: HEADER_HEIGHT_PX + RAIL_GAP_PX + viewHeight,
    keyTonic: frieze.keyTonic,
    signature: frieze.signature,
    barTicks: frieze.barTicks,
    legendWidth: legendWidth(stepPx, frieze.signature),
  };
}

/** Les barres de mesure d'une portée : le quadrillage, sous tout le reste. */
function bars(
  out: ScoreItem[],
  marks: readonly RhythmMark[],
  metrics: ScoreMetrics,
  stave: StavePlace,
  x: (tick: number) => number,
): void {
  const lineTop = linePx(metrics, stave, topStep(stave.staff));
  const lineBottom = linePx(metrics, stave, stave.staff.bottomStep);

  for (const mark of marks) {
    if (!mark.bar) {
      continue;
    }

    const left = x(mark.tick);

    out.push({
      kind: "rule",
      rank: RANK.bar,
      left,
      right: left + 1,
      top: lineTop,
      height: lineBottom - lineTop + 1,
      color: BAR_LINE,
    });
  }
}

/**
 * Une main gravée : ses lignes supplémentaires, ses altérations, ses notes.
 *
 * ## Une note tient jusqu'à la frappe suivante de sa main
 *
 * Et non jusqu'à la fin de sa propre durée : on grave le geste de la main, pas
 * l'enveloppe de chaque touche. Voir `laneOf` dans `rhythm.ts`, qui pose la
 * règle et dit pourquoi.
 *
 * ## Toutes les frappes, et non les seules visibles
 *
 * Une note s'arrête devant l'encre de la suivante — son altération comprise —
 * et la suivante peut être hors fenêtre. C'est ce calcul-là qui donne le blanc
 * que le dessin ne sait pas trouver seul, et c'est aussi pourquoi il a lieu
 * ici, une fois, plutôt qu'à chaque image.
 */
function voice(
  out: ScoreItem[],
  metrics: ScoreMetrics,
  stave: StavePlace,
  x: (tick: number) => number,
): void {
  const laid = stave.segments.map((segment) => ({ segment, ...strikeLead(segment, metrics, x) }));

  laid.forEach((entry, rank) => {
    const { segment, noteLeft, gap } = entry;
    const next = laid[rank + 1];
    const stop = Math.min(x(segment.toTick), next?.inkLeft ?? Infinity) - entry.tail;
    const width = Math.max(stop - noteLeft, metrics.noteHeight);

    for (const key of segment.keys) {
      for (const step of ledgerSteps(key.step, stave.staff)) {
        // Débordante des deux côtés de la note, comme sur une partition gravée :
        // c'est ce qui la fait lire comme un prolongement de la portée et non
        // comme un trait de note.
        const left = noteLeft - LEDGER_OVERHANG_PX;

        out.push({
          kind: "ledger",
          rank: RANK.ledger,
          left,
          right: left + LEDGER_WIDTH_PX,
          top: linePx(metrics, stave, step),
          width: LEDGER_WIDTH_PX,
          color: LEDGER_LINE,
        });
      }
    }

    for (const key of segment.keys) {
      if (key.accidental === null) {
        continue;
      }

      out.push(sign(metrics, stave, key.accidental, key.step, noteLeft - glyphWidth(key.accidental, metrics.stepPx) - gap, stave.color));
    }

    for (const key of segment.keys) {
      out.push({
        kind: "note",
        rank: RANK.note,
        left: noteLeft,
        right: noteLeft + width,
        top: noteTop(metrics, stave, key.step),
        width,
        height: metrics.noteHeight,
        radius: metrics.noteHeight / 2,
        color: ahead(stave.color),
        litColor: stave.lit,
      });
    }
  });
}

/**
 * La règle, sous les deux portées : les temps, les mesures, et leurs numéros.
 *
 * Sous les portées et non entre elles, à la différence des deux voies d'avant :
 * une portée n'a pas de place au milieu, et une graduation posée là se lirait
 * comme une note. Les temps ne sont pas gravés sur une partition, mais ils sont
 * exactement ce que la portée ne dit pas et ce qu'on doit lire ici : les
 * subdivisions ne se déduisent pas d'un dessin sans figures de notes.
 *
 * Les numéros sont ceux du **morceau** et non de la frise : c'est ce qui
 * rattache le passage à la structure du studio et à la minimap, où l'on lit les
 * mêmes chiffres.
 */
function ruler(
  out: ScoreItem[],
  marks: readonly RhythmMark[],
  metrics: ScoreMetrics,
  x: (tick: number) => number,
): void {
  for (const mark of marks) {
    const size = mark.bar ? 5 : 3;
    const left = x(mark.tick) - size / 2;

    out.push({
      kind: "beat",
      rank: RANK.ruler,
      left,
      right: left + size,
      top: metrics.beatsTop + 4 + (mark.bar ? -1 : 0),
      size,
      color: mark.bar ? BAR_DOT : BEAT_DOT,
    });

    if (mark.measure === null) {
      continue;
    }

    // Posé à droite du point de mesure et non centré dessus : un numéro centré
    // sur la barre la chevauche.
    const numberLeft = x(mark.tick) + 6;

    out.push({
      kind: "measure",
      rank: RANK.ruler,
      left: numberLeft,
      right: numberLeft + MEASURE_NUMBER_ROOM_PX,
      top: metrics.beatsTop,
      text: String(mark.measure),
    });
  }
}

/** La place qu'un numéro de mesure occupe, au plus : trois chiffres en mono. */
const MEASURE_NUMBER_ROOM_PX = 24;

/**
 * Le trait d'un changement de tonalité.
 *
 * Quand le couplet passe sur la relative, rien ne le dit sur la page : même
 * armure, autre tonique. La marque prend donc la place que la partition
 * réserve à ce qui change de cadre : la barre de mesure, redoublée d'un trait
 * qui traverse les deux portées, du chiffrage à la règle.
 */
function keyRules(
  out: ScoreItem[],
  marks: readonly FriezeKeyMark[],
  metrics: ScoreMetrics,
  x: (tick: number) => number,
): void {
  const first = metrics.staves[0];

  if (first === undefined) {
    return;
  }

  const top = first.top - KEY_MARK_REACH_PX;
  const height = metrics.beatsTop - top + 2;

  for (const mark of marks) {
    const left = x(mark.tick);

    out.push({
      kind: "rule",
      // Au-dessus des notes, comme dans une partition gravée : un changement de
      // cadre se lit par-dessus ce qu'il encadre.
      rank: RANK.key,
      left,
      right: left + 1,
      top,
      height,
      color: mark.relation === "home" ? KEY_MARK_LINE_HOME : KEY_MARK_LINE,
    });
  }
}

/**
 * L'étendue d'une portée, agrandie de ce que ses signes d'octave demandent.
 *
 * En degrés entiers, arrondis vers l'extérieur : l'étendue donne l'origine des
 * lignes, et une origine à un demi-degré les poserait entre deux pixels.
 */
function withOttavas(
  span: { readonly low: number; readonly high: number },
  ottavas: readonly Ottava[],
  staff: Staff,
): { readonly low: number; readonly high: number } {
  let { low, high } = span;

  for (const ottava of ottavas) {
    const { box } = ottavaGlyph(ottava);
    const rise = (-box.y / UNITS_PER_SPACE) * 2 * OTTAVA_SCALE;
    const drop = ((box.y + box.height) / UNITS_PER_SPACE) * 2 * OTTAVA_SCALE;

    if (ottava.shift === 1) {
      high = Math.max(high, Math.ceil(Math.max(topStep(staff), ottava.edge) + OTTAVA_CLEARANCE_STEPS + rise));
    } else {
      low = Math.min(low, Math.floor(Math.min(staff.bottomStep, ottava.edge) - OTTAVA_CLEARANCE_STEPS - rise - drop));
    }
  }

  return { low, high };
}

/** Le contour d'un signe d'octave : « 8va » au-dessus, « 8vb » au-dessous. */
function ottavaGlyph(ottava: Ottava): (typeof GLYPHS)["ottavaAlta" | "ottavaBassaVb"] {
  return ottava.shift === 1 ? GLYPHS.ottavaAlta : GLYPHS.ottavaBassaVb;
}

/**
 * Où se pose le signe d'un passage, et sa ligne.
 *
 * Au-delà de la note la plus éloignée du passage, ou de la portée si toutes
 * ses notes y sont retombées, à un interligne de blanc : c'est ce qui laisse
 * lire le signe sans le confondre avec une ligne supplémentaire. La ligne passe
 * à mi-hauteur du chiffre, et le crochet va de la ligne vers les notes.
 */
function ottavaPlace(
  metrics: ScoreMetrics,
  stave: StavePlace,
  ottava: Ottava,
): {
  readonly glyph: (typeof GLYPHS)["ottavaAlta" | "ottavaBassaVb"];
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly lineTop: number;
  readonly hook: number;
} {
  const glyph = ottavaGlyph(ottava);
  const scale = glyphScale(metrics.stepPx) * OTTAVA_SCALE;
  const rise = -glyph.box.y * scale;
  const baseline =
    ottava.shift === 1
      ? stepMiddle(metrics, stave, Math.max(topStep(stave.staff), ottava.edge) + OTTAVA_CLEARANCE_STEPS)
      : stepMiddle(metrics, stave, Math.min(stave.staff.bottomStep, ottava.edge) - OTTAVA_CLEARANCE_STEPS) + rise;
  const hook = Math.round(rise / 2);

  return {
    glyph,
    top: baseline + glyph.box.y * scale,
    width: glyph.box.width * scale,
    height: glyph.box.height * scale,
    lineTop: Math.round(baseline - rise / 2),
    hook,
  };
}

/**
 * Les lignes des passages écrits à l'octave, du signe au crochet.
 *
 * Elle part de l'endroit où le signe attend, et non après lui : le signe se
 * colle à la tête et la suit tout le long du passage, sur une plaque de la
 * couleur du fond qui cache les tirets qu'il recouvre. Une ligne qui
 * commencerait après lui s'arrêterait net dès qu'il se met à glisser.
 *
 * Le crochet ferme la ligne à la fin de la dernière note, là où finit ce qui
 * s'écrit à l'octave.
 */
function octaveLines(
  out: ScoreItem[],
  metrics: ScoreMetrics,
  stave: StavePlace,
  x: (tick: number) => number,
): void {
  for (const ottava of stave.ottavas) {
    const place = ottavaPlace(metrics, stave, ottava);
    const { left, right } = octaveExtent(ottava, place, x);

    out.push({
      kind: "octave",
      rank: RANK.ledger,
      left,
      right,
      top: ottava.shift === 1 ? place.lineTop : place.lineTop - place.hook,
      width: right - left,
      height: place.hook,
      up: ottava.shift === -1,
      color: stave.color,
    });
  }
}

/**
 * D'où à où court la ligne d'un passage : du signe qui attend, à la fin de sa
 * dernière note.
 */
function octaveExtent(
  ottava: Ottava,
  place: { readonly width: number },
  x: (tick: number) => number,
): { readonly left: number; readonly right: number } {
  const left = x(ottava.fromTick) + NAME_OFFSET_PX;

  return { left, right: Math.max(left + place.width + OTTAVA_HOOK_CLEAR_PX, x(ottava.toTick) - NOTE_TAIL_GAP_PX) };
}

/**
 * Les signes d'octave d'une portée, qui se collent à la tête comme des noms.
 *
 * C'est ce qui remplace la répétition du signe en tête de chaque système : un
 * passage à l'octave dure souvent plus que ce que le rail montre, et son signe
 * sortirait de l'écran à gauche pendant que ses notes restent décalées sous
 * les yeux. Collé à la tête, il dit en permanence que ce qu'on lit n'est pas
 * ce qu'on entend.
 *
 * Jamais rogné : un nom trop long pour sa fenêtre peut se taire, un signe
 * d'octave non, puisque les notes sont déjà écrites à l'octave.
 */
function ottavaLabels(metrics: ScoreMetrics, stave: StavePlace, x: (tick: number) => number): readonly LabelItem[] {
  return stave.ottavas.map((ottava) => {
    const place = ottavaPlace(metrics, stave, ottava);
    const { box } = place.glyph;
    const line = octaveExtent(ottava, place, x);

    return {
      ...anchored({ fromTick: ottava.fromTick, untilTick: ottava.toTick, sticky: true }, metrics, x, {
        family: stave.hand === "left" ? "ottavaLeft" : "ottavaRight",
        top: place.top,
        centerY: false,
        text: {
          of: "ottava",
          name: ottava.shift === 1 ? "8va" : "8vb",
          path: place.glyph.d,
          viewBox: `${box.x} ${box.y} ${box.width} ${box.height}`,
          width: place.width,
          height: place.height,
          color: stave.color,
        },
      }),
      // Il s'arrête avant le crochet, et non à la fin de l'étendue comme un nom.
      passedRight: -(line.right - OTTAVA_HOOK_CLEAR_PX),
      roomPx: Infinity,
    };
  });
}

/** Les chiffrages, au-dessus de la portée de droite. */
function chordLabels(
  labels: readonly FriezeLabel[],
  metrics: ScoreMetrics,
  x: (tick: number) => number,
): readonly LabelItem[] {
  return labels.map((label) =>
    anchored(label, metrics, x, {
      family: "chord",
      top: metrics.chordTop,
      centerY: false,
      text: {
        of: "chord",
        name: label.name,
        slash: label.slash,
        position: label.inversion === null ? null : positionShort(label.inversion),
        positionColor: INVERSION_COLOR[label.inversion ?? 0] ?? INVERSION_COLOR[0]!,
        notes: label.spelled.map((note) => ({ name: note.name, root: note.root })),
      },
    }),
  );
}

/** Les noms de touches de la main gauche, sur leur portée, à leur hauteur. */
function bassLabels(
  labels: readonly FriezeLabel[],
  metrics: ScoreMetrics,
  x: (tick: number) => number,
): readonly LabelItem[] {
  return labels.map((label) =>
    anchored(label, metrics, x, {
      family: "bass",
      top: bassNameTop(metrics, label),
      centerY: true,
      text: { of: "bass", name: label.name, loop: !label.sticky },
    }),
  );
}

/**
 * Les noms de tonalité, sous la règle.
 *
 * Le nom dit la tonalité et ce qu'elle est au morceau : « Ré mineur · relatif ».
 * C'est le second mot qui enseigne. Le retour à la tonalité du morceau porte
 * la même marque en gris, avec « retour ». Effacé quand la place manque, comme
 * un nom d'accord : une marque tronquée dirait autre chose.
 *
 * Seules les tonalités quittées s'enfuient à la fin de leur plage ; un retour
 * se lit en passant, et reste posé à son trait.
 */
function keyLabels(
  marks: readonly FriezeKeyMark[],
  metrics: ScoreMetrics,
  x: (tick: number) => number,
): readonly LabelItem[] {
  return marks.map((mark) => {
    const away = mark.relation !== "home";
    const aheadLeft = x(mark.tick) + KEY_MARK_NAME_LEFT_PX;
    const passedRight = away ? -(x(mark.untilTick) - NAME_TAIL_PX) : null;

    return {
      kind: "label",
      rank: RANK.key,
      family: "key",
      fromTick: mark.tick,
      untilTick: mark.untilTick,
      aheadLeft,
      passedRight,
      top: metrics.beatsTop,
      centerY: false,
      // Une place et non une mesure : la marque s'efface entièrement quand elle
      // ne tient pas, au lieu de se tronquer.
      roomPx: x(mark.untilTick) - x(mark.tick) - KEY_MARK_NAME_LEFT_PX >= KEY_MARK_MIN_ROOM_PX ? Infinity : 0,
      left: Math.min(aheadLeft, x(mark.untilTick)) - LABEL_CULL_PAD_PX,
      right: x(mark.untilTick) + LABEL_CULL_PAD_PX,
      text: { of: "key", name: `${mark.name} · ${KEY_RELATION_WORD[mark.relation]}`, away },
    };
  });
}

/**
 * Ce qu'on grave autour d'un nom pour décider s'il est à l'écran.
 *
 * Un nom n'a pas de largeur connue avant d'être mesuré, et le découpage a lieu
 * avant. On lui prête donc une marge large : entrer un nom trop tôt ne coûte
 * qu'un nœud, le sortir trop tôt le fait disparaître à l'écran.
 */
const LABEL_CULL_PAD_PX = 320;

function anchored(
  label: Pick<FriezeLabel, "fromTick" | "untilTick" | "sticky">,
  metrics: ScoreMetrics,
  x: (tick: number) => number,
  base: {
    readonly family: LabelItem["family"];
    readonly top: number;
    readonly centerY: boolean;
    readonly text: LabelText;
  },
): LabelItem {
  const aheadLeft = x(label.fromTick) + NAME_OFFSET_PX;
  const passedRight = label.sticky ? -(x(label.untilTick) - NAME_TAIL_PX) : null;

  return {
    kind: "label",
    rank: RANK.label,
    family: base.family,
    fromTick: label.fromTick,
    untilTick: label.untilTick,
    aheadLeft,
    passedRight,
    top: base.top,
    centerY: base.centerY,
    roomPx: x(label.untilTick) - x(label.fromTick) - NAME_OFFSET_PX - NAME_TAIL_PX,
    left: aheadLeft - LABEL_CULL_PAD_PX,
    right: x(label.untilTick) + LABEL_CULL_PAD_PX,
    text: base.text,
  };
}

/**
 * Où le nom d'une touche de la main gauche se pose, verticalement.
 *
 * À la hauteur de sa note, et au milieu de la portée quand la frise n'en donne
 * pas : un nom sans hauteur vaut mieux qu'un nom hors de sa portée.
 */
function bassNameTop(metrics: ScoreMetrics, label: FriezeLabel): number {
  const stave = bassOf(metrics);

  return label.step === null ? stave.top + stave.height / 2 : stepMiddle(metrics, stave, label.step);
}

/**
 * Où commencent les notes d'une frappe, une fois la place faite aux signes.
 *
 * ## Les altérations se rangent à droite, contre les notes
 *
 * Un accord peut en porter plusieurs, et une partition les aligne sur la même
 * droite : c'est le bord gauche des notes. Chacune est donc posée en reculant
 * depuis là, et non en avançant depuis un point fixe — sinon un bécarre, plus
 * étroit qu'un dièse, laisserait un trou entre lui et sa note.
 *
 * ## Aucune ne passe la barre de mesure
 *
 * Une note du premier temps aurait la sienne dans la mesure d'avant, où elle
 * ne veut rien dire — et par-dessus la barre, qu'elle salit. Les notes reculent
 * donc juste ce qu'il faut pour que le plus large des signes tienne après la
 * barre. C'est le seul endroit où l'abscisse cesse d'être exactement le temps,
 * et c'est ce que fait une partition gravée : le signe appartient à la note,
 * donc il tient dans son emplacement, pas dans celui d'avant.
 */
function strikeLead(
  segment: FriezeSegment,
  metrics: ScoreMetrics,
  x: (tick: number) => number,
): {
  readonly noteLeft: number;
  readonly inkLeft: number;
  readonly gap: number;
  readonly tail: number;
} {
  const slot = x(segment.toTick) - x(segment.fromTick);
  const squeeze = Math.min(1, (slot * INK_SHARE) / (NOTE_LEAD_IN_PX + NOTE_TAIL_GAP_PX));
  const tail = NOTE_TAIL_GAP_PX * squeeze;
  const gap = SIGN_TO_NOTE_SPACES * metrics.stepPx * 2;
  const plain = x(segment.fromTick) + NOTE_LEAD_IN_PX * squeeze;
  const widest = segment.keys.reduce(
    (wide, key) => (key.accidental === null ? wide : Math.max(wide, glyphWidth(key.accidental, metrics.stepPx) + gap)),
    0,
  );

  if (widest === 0) {
    return { noteLeft: plain, inkLeft: plain, gap, tail };
  }

  const floor =
    metrics.barTicks <= 0
      ? -Infinity
      : x(Math.floor(segment.fromTick / metrics.barTicks) * metrics.barTicks) + BAR_CLEARANCE_PX + widest;
  const noteLeft = Math.max(plain, floor);

  return { noteLeft, inkLeft: noteLeft - widest, gap, tail };
}

/**
 * Les signes de la marge : la clé de chaque portée, puis l'armure.
 *
 * Dans le cadre et non dans le ruban : ils ne défilent pas, et ils sont donc
 * gravés une fois pour toutes au montage.
 */
function legendSigns(metrics: ScoreMetrics): readonly SignItem[] {
  const clefWidth = Math.max(GLYPHS.gClef.box.width, GLYPHS.fClef.box.width) * glyphScale(metrics.stepPx);
  const out: SignItem[] = [];

  for (const stave of metrics.staves) {
    out.push(
      sign(
        metrics,
        stave,
        stave.clef === "treble" ? "gClef" : "fClef",
        stave.staff.bottomStep + (stave.clef === "treble" ? 2 : 6),
        LEGEND_PAD_PX,
        stave.color,
      ),
    );

    const signature = metrics.signature;

    if (signature === null) {
      continue;
    }

    signatureSteps(signature, stave.clef, stave.staff.bottomStep).forEach((step, rank) => {
      out.push(
        sign(
          metrics,
          stave,
          signature.accidental,
          step,
          LEGEND_PAD_PX + clefWidth + LEGEND_PAD_PX + rank * signatureAdvance(signature.accidental, metrics.stepPx),
          stave.color,
        ),
      );
    });
  }

  return out;
}

/**
 * Un signe posé sur une portée : une clé, une altération.
 *
 * Les contours de `glyphs.ts` ont pour origine le point qui doit tomber sur la
 * ligne : la boucle de la clé de sol, les deux points de la clé de fa, le
 * centre d'une altération. Le `viewBox` est donc la boîte du contour, et le
 * bloc est décalé de cette boîte — ce qui fait tomber l'origine, et elle seule,
 * à l'endroit demandé. Aucune constante de calage à régler, et rien à reprendre
 * quand la portée se resserre.
 */
function sign(
  metrics: ScoreMetrics,
  stave: StavePlace,
  glyph: keyof typeof GLYPHS,
  step: number,
  originLeft: number,
  color: string,
): SignItem {
  const { d, box } = GLYPHS[glyph];
  const scale = glyphScale(metrics.stepPx);
  const left = originLeft + box.x * scale;
  const width = box.width * scale;

  return {
    kind: "sign",
    rank: RANK.sign,
    left,
    right: left + width,
    top: stepMiddle(metrics, stave, step) + box.y * scale,
    width,
    height: box.height * scale,
    viewBox: `${box.x} ${box.y} ${box.width} ${box.height}`,
    path: d,
    color,
  };
}

// ── L'index : ce qui est à l'écran, sans balayer le niveau ───────

/**
 * Ranger les objets par colonnes, chacun dans toutes celles qu'il traverse.
 *
 * Le prix est une entrée par colonne traversée, ce qui est un petit multiple du
 * nombre d'objets ; le gain est que lire une fenêtre coûte ce qu'elle contient,
 * et non ce que le niveau contient. C'est ce qui rend le coût d'un défilement
 * indépendant de la longueur du morceau.
 */
function index(items: readonly ScoreItem[]): readonly (readonly ScoreItem[])[] {
  const last = items.reduce((wide, item) => Math.max(wide, item.right), 0);
  const count = Math.max(1, Math.ceil(last / BUCKET_PX) + 1);
  const buckets: ScoreItem[][] = Array.from({ length: count }, () => []);

  for (const item of items) {
    const from = Math.max(0, Math.floor(item.left / BUCKET_PX));
    const to = Math.min(count - 1, Math.floor(item.right / BUCKET_PX));

    for (let at = from; at <= to; at += 1) {
      buckets[at]!.push(item);
    }
  }

  return buckets;
}

/**
 * Ce qui croise une fenêtre, sans doublon et dans l'ordre de peinture.
 *
 * Un objet posé dans plusieurs colonnes y est vu plusieurs fois ; le marqueur
 * `seen` le retient une seule fois, et il est passé par l'appelant pour qu'il
 * n'y ait pas d'allocation par image.
 */
export function itemsIn(
  layout: ScoreLayout,
  fromPx: number,
  toPx: number,
  seen: Set<ScoreItem> = new Set(),
): Set<ScoreItem> {
  seen.clear();

  const first = Math.max(0, Math.floor(fromPx / BUCKET_PX));
  const last = Math.min(layout.buckets.length - 1, Math.floor(toPx / BUCKET_PX));

  for (let at = first; at <= last; at += 1) {
    for (const item of layout.buckets[at]!) {
      if (item.right >= fromPx && item.left <= toPx) {
        seen.add(item);
      }
    }
  }

  return seen;
}

// ── Les repères verticaux ────────────────────────────────────────

/** La portée de la main gauche, celle qui porte les noms de touches. */
export function bassOf(metrics: ScoreMetrics): StavePlace {
  return metrics.staves[metrics.staves.length - 1]!;
}

/** Le haut du corps d'une note, dans le rail. */
export function noteTop(metrics: ScoreMetrics, stave: StavePlace, step: number): number {
  return stave.top + (stave.highStep - step) * metrics.stepPx;
}

/** Le milieu d'un degré, dans le rail : c'est là que passe une ligne. */
export function stepMiddle(metrics: ScoreMetrics, stave: StavePlace, step: number): number {
  return noteTop(metrics, stave, step) + metrics.noteHeight / 2;
}

/**
 * Où poser un trait d'un pixel pour qu'il soit **centré** sur son degré.
 *
 * Un trait d'un pixel posé à l'ordonnée `y` occupe la bande `[y, y+1)` : son
 * milieu est à `y + 0,5`, et non à `y`. Arrondir le milieu du degré et s'en
 * servir comme ordonnée décalait donc chaque ligne d'un demi-pixel vers le bas,
 * et comme le corps d'une note fait sept pixels — un nombre impair, dont le
 * milieu tombe sur un demi-pixel — l'arrondi ajoutait un second demi-pixel dans
 * le même sens. Les notes se lisaient un pixel trop haut sur toute la portée,
 * ce qui se voit : c'est un septième du corps.
 *
 * On pose donc le trait par son milieu, et à la taille de référence — sept
 * pixels le degré — l'accord est exact. Aux deux tailles inférieures, le corps
 * est pair et son milieu tombe sur un entier, qu'un trait d'un pixel ne peut
 * pas viser : il reste alors un demi-pixel, qui est le mieux qu'on puisse
 * faire sans dessiner un trait flou.
 */
export function linePx(metrics: ScoreMetrics, stave: StavePlace, step: number): number {
  return Math.round(stepMiddle(metrics, stave, step) - 0.5);
}

/**
 * La portée où poser une hauteur que la partition n'écrit pas, ici.
 *
 * Le do central partage, comme sur n'importe quelle partition de piano : c'est
 * le seul repère qui ne demande pas de savoir quelle main a joué, et le clavier
 * ne le dit pas.
 */
export function staveForStep(metrics: ScoreMetrics, step: number): StavePlace {
  const treble = metrics.staves[0]!;

  return step >= TREBLE.bottomStep - 2 ? treble : bassOf(metrics);
}

/** Combien de pixels vaut une unité de contour, à cette taille de degré. */
export function glyphScale(stepPx: number): number {
  // Un interligne vaut deux degrés, et `UNITS_PER_SPACE` unités.
  return (stepPx * 2) / UNITS_PER_SPACE;
}

/** D'un signe d'armure au suivant, en pixels. */
function signatureAdvance(accidental: "sharp" | "flat", stepPx: number): number {
  return signatureAdvanceSteps(accidental) * stepPx;
}

/** La largeur d'un contour, en pixels. */
function glyphWidth(name: Accidental | "gClef" | "fClef", stepPx: number): number {
  return GLYPHS[name].box.width * glyphScale(stepPx);
}

/**
 * La largeur de la tête, mesurée sur ce qu'elle contient.
 *
 * La plus large des deux clés, et non celle de chaque portée : les deux
 * armures doivent commencer au même endroit, sans quoi les deux portées ne
 * s'aligneraient plus et la tête se lirait de travers.
 */
function legendWidth(stepPx: number, signature: KeySignature | null): number {
  const scale = glyphScale(stepPx);
  const clef = Math.max(GLYPHS.gClef.box.width, GLYPHS.fClef.box.width) * scale;
  const signs =
    signature === null || signature.count === 0
      ? 0
      : (signature.count - 1) * signatureAdvance(signature.accidental, stepPx) +
        glyphWidth(signature.accidental, stepPx);

  return Math.ceil(LEGEND_PAD_PX + clef + LEGEND_PAD_PX + signs + LEGEND_PAD_PX);
}
